import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import {
  Button,
  Caption1,
  Dialog,
  Header3,
  InfoSmallIcon,
  Input,
  InputPassword,
  Label,
  Tooltip,
} from "@activecollab/components";
import { Field, Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { showErrorToast } from "~/components/Toast";
import { useAddApiToken } from "~/repository";

const ApiTokenAddDialog = ({ open, onClose, userId, setSelectedToken }) => {
  const { t } = useTranslation();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const addTokenMutation = useAddApiToken();
  const client = useQueryClient();

  const handleAddToken = useCallback(
    (data) => {
      addTokenMutation.mutate(
        { userId, data },
        {
          onSuccess: (result) => {
            setSelectedToken(result.single);
            onClose();
            client.invalidateQueries(["api-tokens"]);
          },
          onError: () => {
            showErrorToast(t("Unable to add token"));
          },
        }
      );
    },
    [addTokenMutation, client, onClose, setSelectedToken, t, userId]
  );

  const validationSchema = yup.object().shape({
    password: yup.string().trim().required(t("Password is required")),
    client_name: yup.string().trim().required(t("Client name is required")),
    client_vendor: yup.string().trim().required(t("Vendor name is required")),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <Formik
        initialValues={{ password: "", client_name: "", client_vendor: "" }}
        onSubmit={handleAddToken}
        validateOnMount
        validationSchema={validationSchema}
      >
        {({ errors, touched, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            handleSubmit();
            if (!Object.keys(errors)?.length) return;
            focusFirstInvalidField(errors, false);
          };
          return (
            <Form onSubmit={handleFormSubmit}>
              <Dialog.Title disableDefaultHeading>
                <Header3>{t("Add Token")}</Header3>
              </Dialog.Title>
              <Dialog.ContentDivider />
              <Dialog.Content>
                <div className="tw-flex tw-flex-col tw-gap-4">
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <Field name="password">
                      {({ field, form }) => (
                        <>
                          <Label
                            htmlFor="password"
                            invalid={errors.password && touched.password}
                          >
                            {t("Your Password")}*
                          </Label>
                          <InputPassword
                            id="password"
                            autoFocus
                            name={field.name}
                            value={field.value}
                            invalid={errors.password && touched.password}
                            autoComplete="new-password"
                            onChange={(value) => {
                              form.setFieldTouched(field.name, false);
                              form.setFieldValue(field.name, value);
                            }}
                            ref={(el) => (fieldRef.current.password = el)}
                            errorMessage={errors.password && touched.password ? errors.password : null}
                          />
                          <Caption1 color="tertiary">{t("We need to make sure it’s you")}</Caption1>
                        </>
                      )}
                    </Field>
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <Field name="client_name">
                      {({ field, form }) => (
                        <>
                          <Label
                            htmlFor="client_name"
                            invalid={errors.client_name && touched.client_name}
                          >
                            {t("Client")}*
                          </Label>
                          <Input
                            id="client_name"
                            name={field.name}
                            value={field.value}
                            invalid={errors.client_name && touched.client_name}
                            ref={(el) => (fieldRef.current.client_name = el)}
                            onChange={(e) => {
                              form.setFieldTouched(field.name, false);
                              form.setFieldValue(field.name, e.target.value);
                            }}
                            endAdornment={
                              touched.client_name && errors.client_name ? (
                                <Tooltip title={errors.client_name}>
                                  <InfoSmallIcon
                                    fill="var(--red-alert)"
                                    className="tw-cursor-help tw-mr-1"
                                  />
                                </Tooltip>
                              ) : null
                            }
                          />
                          <Caption1 color="tertiary">{t("Name of the app that will use this token")}</Caption1>
                        </>
                      )}
                    </Field>
                  </div>
                  <div className="tw-flex tw-flex-col tw-gap-1">
                    <Field name="client_vendor">
                      {({ field, form }) => (
                        <>
                          <Label
                            htmlFor="client_vendor"
                            invalid={errors.client_vendor && touched.client_vendor}
                          >
                            {t("Vendor")}*
                          </Label>
                          <Input
                            id="client_vendor"
                            name={field.name}
                            value={field.value}
                            invalid={errors.client_vendor && touched.client_vendor}
                            ref={(el) => (fieldRef.current.client_vendor = el)}
                            onChange={(e) => {
                              form.setFieldTouched(field.name, false);
                              form.setFieldValue(field.name, e.target.value);
                            }}
                            endAdornment={
                              touched.client_vendor && errors.client_vendor ? (
                                <Tooltip title={errors.client_vendor}>
                                  <InfoSmallIcon
                                    fill="var(--red-alert)"
                                    className="tw-cursor-help tw-mr-1"
                                  />
                                </Tooltip>
                              ) : null
                            }
                          />
                          <Caption1 color="tertiary">{t("Name of the organization that made the app")}</Caption1>
                        </>
                      )}
                    </Field>
                  </div>
                </div>
              </Dialog.Content>
              <Dialog.ContentDivider />
              <Dialog.Actions>
                <Button
                  className="tw-mr-3"
                  disabled={addTokenMutation.isLoading}
                  type="submit"
                >
                  {t("Add Token")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onClose}
                  disabled={addTokenMutation.isLoading}
                >
                  {t("Cancel")}
                </Button>
              </Dialog.Actions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

ApiTokenAddDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  userId: PropTypes.number,
  setSelectedToken: PropTypes.func,
};

export default ApiTokenAddDialog;
